import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { BiddingBlockCsvRowResult } from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import { BiddingCsvRowStatus } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { BiddingSpotCsvRowResult } from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import i18next from 'i18next'

import { convertAreaLabel } from '~/models/area'
import { BLOCK_CSV_TYPES, type BlockBidCsvKind } from '~/models/bidCsvHeader'
import { civilDateToInputValue } from '~/models/civilDate'
import {
  formatKomaCompactLabel,
  formatKomaRangeLabel,
} from '~/models/komaLabel'
import type {
  BidCsvPreviewCell,
  BidCsvPreviewRow,
} from '~/pages/SpotCsvImport/components/PreviewTable'

/** 売買方向の表示ラベル (画面共通の `common:tradeSide.*` を使う)。 */
const sideLabel = (side: Side): string => {
  if (side === Side.BUY) return i18next.t('common:tradeSide.buy')
  if (side === Side.SELL) return i18next.t('common:tradeSide.sell')
  return ''
}

const isKomaNumber = (koma: number): boolean =>
  Number.isInteger(koma) && koma >= 1 && koma <= 48

const komaCell = (koma: number): string => {
  if (koma === 0) return ''
  return isKomaNumber(koma)
    ? formatKomaCompactLabel(i18next.t.bind(i18next), koma)
    : String(koma)
}

const blockKomaRangeCell = (start: number, end: number): string => {
  if (start === 0) return ''
  const range = `${start}〜${end}`
  if (!isKomaNumber(start) || !isKomaNumber(end)) return range

  return formatKomaRangeLabel(i18next.t.bind(i18next), start, end)
}

/**
 * 発注主体区分の表示ラベル (CSV の `発注主体区分` 列の値。 spec
 * spot-bid-normal-csv-import.md §2 列 3 / spot-bid-block-csv-import.md §2:
 * 需要BG = 小売 / 発電 / 自己託送BG = 自己託送)。 通常入札・ブロック入札で共通。
 */
const issuerTypeLabel = (issuerType: LicenseType): string => {
  if (issuerType === LicenseType.RETAIL)
    return i18next.t('page:spotCsvImport.issuerTypeLabel.retail')
  if (issuerType === LicenseType.GENERATION)
    return i18next.t('page:spotCsvImport.issuerTypeLabel.generation')
  if (issuerType === LicenseType.SELF_WHEELING)
    return i18next.t('page:spotCsvImport.issuerTypeLabel.selfWheeling')
  return ''
}

/**
 * JEPX自由記述欄の表示値。 排出係数紐付け = 1 の行はシステムが組み立てた判別コメント
 * なので、 CSV に書いた英字 1 文字ではないことが分かるよう注記を添える
 * (通常入札 AC-SPOT-019 / ブロック入札 AC-DSB-030。 同じ表で表示を割らない)。
 */
const memoJepxCell = (
  memoJepx: string,
  emissionFactorLinked: boolean,
): BidCsvPreviewCell => {
  if (!emissionFactorLinked) return memoJepx
  return {
    text: memoJepx,
    annotation: i18next.t('page:spotCsvImport.emissionCommentAnnotation'),
  }
}

/**
 * 価格列の表示値。 手数料自動考慮 ON の指値行は `価格` 列の値 (希望価格) の下に
 * 算出済みの入札価格を補足表示し、 利用者が入力した希望価格ではなくシステムが
 * 算出した値であることが分かるよう区別する (spec spot-bid-normal-csv-import.md
 * §2 / `bidding/spot-bidding.md` AC-BSP-004b。 表示の形は AC-SPOT-019 の判別
 * コメント注記と揃える)。 OFF・成行の行 (order_price_yen_per_kwh 未設定) は
 * 従来どおり価格列の値のみ。
 */
const priceCell = (row: BiddingSpotCsvRowResult): BidCsvPreviewCell => {
  const price = row.priceYenPerKwh?.value ?? ''
  const orderPrice = row.orderPriceYenPerKwh?.value
  if (orderPrice === undefined) return price
  return {
    text: price,
    annotation: i18next.t('page:spotCsvImport.orderPriceAnnotation', {
      orderPrice,
    }),
  }
}

/** CSV 取込画面の対象種別 (`?kind=` クエリ)。 */
export type BidCsvKind = 'normal' | 'block'

/**
 * `?kind=` を種別へ解決する。未指定・未知値は通常入札にフォールバックする
 * (spec spot-bidding.md AC-BSP-006 の遷移先は `?kind=normal` が既定)。
 */
export const bidCsvKindFromSearchParam = (raw: string | null): BidCsvKind =>
  raw === 'block' ? 'block' : 'normal'

/**
 * `?blockType=` を導線の種別 (ブロック入札タブの「ブロック作成 > CSVインポート」の導線ごと) へ
 * 解決する。 未指定・未知値は通常ブロック。
 *
 * 取込はどの導線からでも通常ブロック (56 列)・リンクブロック・ループ (58 列) の
 * 4 種類のヘッダーを受理し、 列構成は backend がヘッダーで、 リンクかループかは
 * ブロック種別の値で決める (spec spot-bid-block-csv-import.md AC-DSB-012 / AC-DSB-013)。
 * この値は Step1 の案内とヘッダー不一致のエラーに示す列名にだけ使う (backend へは送らない)。
 */
export const blockCsvTypeFromSearchParam = (
  raw: string | null,
): BlockBidCsvKind => BLOCK_CSV_TYPES.find((type) => type === raw) ?? 'standard'

/**
 * 通常入札のプレビュー行を表示用に整形する。
 * 値は backend が解決済みのものをそのまま表示する (frontend で再計算しない)。
 */
export const toSpotPreviewRows = (
  rows: BiddingSpotCsvRowResult[],
): BidCsvPreviewRow[] =>
  rows.map((row) => ({
    rowNumber: row.rowNumber,
    status: row.status,
    message: row.message,
    warning: row.duplicateWarning,
    columns: [
      civilDateToInputValue(row.targetDate),
      komaCell(row.timeCode),
      issuerTypeLabel(row.issuerType),
      convertAreaLabel(row.areaCode),
      sideLabel(row.direction),
      // 成行は価格を持たない (AC-SPOT-006)。 手数料自動考慮 ON の指値行は
      // priceCell が算出済みの入札価格を補足表示する (AC-BSP-004b)。
      priceCell(row),
      row.volumeMw?.value ?? '',
      memoJepxCell(row.memoJepx ?? '', row.emissionFactorLinked),
    ],
  }))

/**
 * 価格列の表示値。 手数料自動考慮 ON の行は `価格` 列の値 (希望価格) の下に
 * 算出済みの入札価格を補足表示し、 利用者が入力した希望価格ではなくシステムが
 * 算出した値であることが分かるよう区別する (spec spot-bid-block-csv-import.md
 * §2 / `bidding/spot-bidding.md` AC-BSP-004b。 通常入札 CSV の priceCell と
 * 同じ形)。 `order_price_yen_per_kwh` は価格列の値、 算出済みの入札価格は
 * `fee_adjusted_order_price_yen_per_kwh` (ON の行のみ)。 OFF の行は従来どおり価格列の値のみ。
 */
const blockPriceCell = (row: BiddingBlockCsvRowResult): BidCsvPreviewCell => {
  const price = row.orderPriceYenPerKwh?.value ?? ''
  const orderPrice = row.feeAdjustedOrderPriceYenPerKwh?.value
  if (orderPrice === undefined) return price
  return {
    text: price,
    annotation: i18next.t('page:spotCsvImport.orderPriceAnnotation', {
      orderPrice,
    }),
  }
}

/**
 * ブロック入札のプレビュー行を表示用に整形する。 列の並びは画面の
 * `dataColumns` (index.tsx) と 1:1。 エラー行は値を持たないので空欄になる。
 */
export const toBlockPreviewRows = (
  rows: BiddingBlockCsvRowResult[],
): BidCsvPreviewRow[] =>
  rows.map((row) => {
    const created = row.status === BiddingCsvRowStatus.CREATED
    return {
      rowNumber: row.rowNumber,
      status: row.status,
      message: row.message,
      warning: row.duplicateWarning,
      columns: [
        civilDateToInputValue(row.targetDate),
        created ? issuerTypeLabel(row.issuerType) : '',
        convertAreaLabel(row.areaCode),
        sideLabel(row.direction),
        blockPriceCell(row),
        blockKomaRangeCell(row.startTimeCode, row.endTimeCode),
        memoJepxCell(row.jepxFreeText, row.emissionFactorLinked),
        row.memo,
        row.pairNo === '' ? '' : `${row.pairNo} (${row.blockType})`,
      ],
    }
  })
